import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

type ReportLookupState =
  | { state: 'loading'; message: string }
  | { state: 'ready'; pdfUrl: string; requestId: string; reportId: string; contactName: string; companyName: string }
  | { state: 'used'; message: string }
  | { state: 'expired'; message: string }
  | { state: 'not_found'; message: string }
  | { state: 'error'; message: string };

export function TestDriveReport() {
  const { token = '' } = useParams<{ token: string }>();
  const [result, setResult] = useState<ReportLookupState>({
    state: 'loading',
    message: 'Validating report link...',
  });

  const endpoint = useMemo(() => {
    const envApi = ((import.meta as any)?.env?.VITE_TEST_DRIVE_API as string | undefined)?.trim();
    const base = envApi || 'https://kohic3mczj.execute-api.us-east-1.amazonaws.com/prod';
    return `${base.replace(/\/$/, '')}/website/test-drive/report/${encodeURIComponent(token)}`;
  }, [token]);

  useEffect(() => {
    let isMounted = true;
    async function load() {
      if (!token) {
        setResult({ state: 'not_found', message: 'Missing report token.' });
        return;
      }
      try {
        const response = await fetch(endpoint, { method: 'GET' });
        const payload = (await response.json().catch(() => ({}))) as Record<string, unknown>;
        const error = String(payload.error ?? '').trim();
        if (!response.ok || payload.success !== true) {
          if (!isMounted) return;
          if (error === 'report_link_used') {
            setResult({ state: 'used', message: 'This report link was already used.' });
            return;
          }
          if (error === 'report_link_expired') {
            setResult({ state: 'expired', message: 'This report link has expired.' });
            return;
          }
          if (error === 'report_not_found') {
            setResult({ state: 'not_found', message: 'Report link not found.' });
            return;
          }
          setResult({ state: 'error', message: error || `Report lookup failed (${response.status}).` });
          return;
        }
        const pdfUrl = String(payload.pdfUrl ?? '').trim();
        if (!pdfUrl) {
          if (isMounted) setResult({ state: 'error', message: 'Report URL is missing.' });
          return;
        }
        if (!isMounted) return;
        setResult({
          state: 'ready',
          pdfUrl,
          requestId: String(payload.requestId ?? ''),
          reportId: String(payload.reportId ?? ''),
          contactName: String(payload.contactName ?? ''),
          companyName: String(payload.companyName ?? ''),
        });
        window.location.assign(pdfUrl);
      } catch (error) {
        if (!isMounted) return;
        setResult({
          state: 'error',
          message: error instanceof Error ? error.message : String(error),
        });
      }
    }
    void load();
    return () => {
      isMounted = false;
    };
  }, [endpoint, token]);

  return (
    <div className="min-h-screen bg-dark-bg text-white">
      <div className="max-w-2xl mx-auto px-6 py-16">
        <div className="rounded-xl border border-border-dark bg-card-dark p-8">
          <h1 className="text-2xl font-bold mb-3">Test Drive Report</h1>
          {result.state === 'loading' && <p className="text-gray-300">{result.message}</p>}
          {result.state === 'ready' && (
            <div className="space-y-3">
              <p className="text-gray-300">
                Opening report for <span className="text-white">{result.contactName || 'Contact'}</span>
                {result.companyName ? ` at ${result.companyName}` : ''}.
              </p>
              <a
                href={result.pdfUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-block px-4 py-2 rounded bg-red-600 hover:bg-red-500 font-semibold"
              >
                Open PDF
              </a>
              <p className="text-xs text-gray-500 break-all">Request: {result.requestId} | Report: {result.reportId}</p>
            </div>
          )}
          {(result.state === 'used' || result.state === 'expired' || result.state === 'not_found' || result.state === 'error') && (
            <div className="rounded-md border border-rose-500/40 bg-rose-950/40 px-3 py-2 text-sm text-rose-200">
              {result.message}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

